import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loader2, X, Sparkles, Brain, RotateCw, Check } from "lucide-react";
import { toast } from "sonner";
import { fireConfettiCannons } from "@/lib/confetti";

/**
 * FlashcardDeck — Phase 4 / Iter75.
 *
 * Full-screen swipe deck for spaced-repetition review. Pulls from
 * `/api/flashcards/daily` on open. The user taps the card to reveal
 * the answer, then rates: Again / Hard / Good / Easy. The card slides
 * off in a direction proportional to the rating, the next card eases
 * in beneath it, XP increments, and the deck advances.
 *
 * Modes
 * -----
 * - `daily` (default): pulls today's due + new cards from the backend.
 * - `step`: pulls a freshly-generated deck for a specific (path, step).
 *   Used by the "Make flashcards" CTA on a lesson page so the user can
 *   review immediately without leaving the reading flow.
 *
 * Why a full-screen modal (not a sheet): minor users can be in a noisy
 * environment; the dimmed overlay nudges focus on a single card. The
 * UI is keyboard-friendly: SPACE to flip, 1–4 to rate, ESC to close.
 */
export default function FlashcardDeck({
  open,
  onOpenChange,
  mode = "daily",
  pathId = null,
  stepIndex = null,
  onComplete,
}) {
  const [phase, setPhase] = useState("loading"); // loading | empty | reviewing | done | error
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [creditGranted, setCreditGranted] = useState(0);

  // Load deck on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPhase("loading");
    setCards([]);
    setIdx(0);
    setFlipped(false);
    setReviewedCount(0);
    setCreditGranted(0);

    const load = async () => {
      try {
        if (mode === "step" && pathId != null && stepIndex != null) {
          const { data } = await api.post("/flashcards/generate-from-step", {
            path_id: pathId,
            step_index: stepIndex,
          });
          if (cancelled) return;
          const list = data?.cards || [];
          setCards(list);
          setPhase(list.length ? "reviewing" : "empty");
        } else {
          const { data } = await api.get("/flashcards/daily");
          if (cancelled) return;
          const list = data?.cards || [];
          setCards(list);
          setPhase(list.length ? "reviewing" : "empty");
        }
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status !== 402) {
          toast.error(e?.response?.data?.detail || "Couldn't load flashcards");
        }
        setPhase("error");
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, mode, pathId, stepIndex]);

  // Keyboard shortcuts: SPACE to flip, 1-4 to rate, ESC to close.
  useEffect(() => {
    if (!open || phase !== "reviewing") return;
    const RATING_MAP = { 1: "again", 2: "hard", 3: "good", 4: "easy" };
    const onKey = (e) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        setFlipped((f) => !f);
        return;
      }
      if (flipped && RATING_MAP[e.key]) {
        e.preventDefault();
        submitRating(RATING_MAP[e.key]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, flipped, idx]);

  const current = cards[idx];
  const total = cards.length;
  const isLast = idx >= total - 1;

  const submitRating = async (rating) => {
    if (!current || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/flashcards/review", {
        card_id: current.card_id,
        rating,
      });
      // XP — fire the global event so XPBadge re-fetches snapshot.
      if (data?.xp?.leveled_up) {
        window.dispatchEvent(new CustomEvent("oak:level-up", {
          detail: data.xp,
        }));
      }
      window.dispatchEvent(new Event("oak:xp-updated"));
      const newReviewed = reviewedCount + 1;
      setReviewedCount(newReviewed);

      if (isLast) {
        // Try claim the daily +1 credit.
        try {
          const { data: claim } = await api.post("/flashcards/complete-daily", {
            reviewed_count: newReviewed,
          });
          if (claim?.granted > 0) {
            setCreditGranted(claim.granted);
            window.dispatchEvent(new CustomEvent("oak:credits-updated", {
              detail: { credits: claim.credits },
            }));
            fireConfettiCannons();
          }
        } catch { /* daily already claimed — no-op */ }
        setPhase("done");
        onComplete?.();
      } else {
        // small advance pause so the user sees the flip → rate dopamine
        setTimeout(() => {
          setIdx((i) => i + 1);
          setFlipped(false);
        }, 220);
      }
    } catch (e) {
      toast.error("Couldn't save your review — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => onOpenChange?.(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0B0E14]/95 border-white/10 text-white max-w-xl p-0 overflow-hidden"
        data-testid="flashcard-deck"
      >
        <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 grid place-items-center rounded-lg bg-[#0044FF]/12 border border-[#0044FF]/35">
              <Brain size={15} className="text-[#9bc7ff]" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-sm tracking-tight">
                {mode === "step" ? "Lesson Flashcards" : "Daily Flashcards"}
              </div>
              <div className="text-[10.5px] text-white/45 font-mono-oak">
                {phase === "reviewing"
                  ? `Card ${Math.min(idx + 1, total)} of ${total}`
                  : phase === "done" ? "Deck complete" : "Spaced-repetition review"}
              </div>
            </div>
          </div>
          <button
            onClick={close}
            className="h-7 w-7 grid place-items-center rounded-md text-white/45 hover:text-white hover:bg-white/5"
            aria-label="Close"
            data-testid="flashcard-close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Progress bar */}
        {phase === "reviewing" && (
          <div className="h-1 bg-white/5">
            <motion.div
              className="h-full bg-gradient-to-r from-[#0044FF] via-[#5D3FD3] to-[#00FF9D]"
              initial={{ width: 0 }}
              animate={{ width: `${(idx / Math.max(1, total)) * 100}%` }}
              transition={{ duration: 0.35 }}
              data-testid="flashcard-progress"
            />
          </div>
        )}

        <div className="px-5 py-6 min-h-[360px] grid place-items-center">
          {phase === "loading" && (
            <div className="text-white/55 text-sm flex items-center gap-2" data-testid="flashcard-loading">
              <Loader2 size={16} className="animate-spin" /> Preparing your deck…
            </div>
          )}

          {phase === "empty" && (
            <EmptyDeckState mode={mode} onClose={close} />
          )}

          {phase === "error" && (
            <div className="text-white/55 text-sm text-center" data-testid="flashcard-error">
              Couldn't load flashcards. Generate a lesson first, then try again.
            </div>
          )}

          {phase === "reviewing" && current && (
            <CardView
              key={current.card_id}
              card={current}
              flipped={flipped}
              onFlip={() => setFlipped((f) => !f)}
            />
          )}

          {phase === "done" && (
            <DoneState
              reviewedCount={reviewedCount}
              creditGranted={creditGranted}
              onClose={close}
            />
          )}
        </div>

        {phase === "reviewing" && (
          <div
            className="px-5 pb-5 pt-1 border-t border-white/8 flex flex-col gap-2.5"
            data-testid="flashcard-controls"
          >
            {!flipped ? (
              <button
                onClick={() => setFlipped(true)}
                disabled={submitting}
                className="w-full py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white font-display text-sm transition"
                data-testid="flashcard-reveal"
              >
                Tap to reveal answer · or press <kbd className="px-1 py-0.5 text-[10px] bg-white/10 rounded font-mono-oak">Space</kbd>
              </button>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                <RateButton id="again" label="Again" hint="1" tone="rose" onClick={() => submitRating("again")} disabled={submitting} />
                <RateButton id="hard" label="Hard" hint="2" tone="amber" onClick={() => submitRating("hard")} disabled={submitting} />
                <RateButton id="good" label="Good" hint="3" tone="cobalt" onClick={() => submitRating("good")} disabled={submitting} />
                <RateButton id="easy" label="Easy" hint="4" tone="mint" onClick={() => submitRating("easy")} disabled={submitting} />
              </div>
            )}
            <div className="text-[10px] text-white/35 font-mono-oak text-center">
              Honest ratings give you smarter reviews tomorrow.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CardView({ card, flipped, onFlip }) {
  return (
    <motion.button
      type="button"
      onClick={onFlip}
      className="relative w-full max-w-md aspect-[5/3] rounded-2xl border border-white/12 bg-gradient-to-br from-[#0F1320] to-[#0B0E14] cursor-pointer overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#00FF9D]/40"
      whileTap={{ scale: 0.985 }}
      data-testid="flashcard-card"
    >
      <AnimatePresence mode="wait" initial={false}>
        {!flipped ? (
          <motion.div
            key="q"
            initial={{ opacity: 0, rotateY: -12 }}
            animate={{ opacity: 1, rotateY: 0 }}
            exit={{ opacity: 0, rotateY: 12 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 p-6 flex flex-col"
            data-testid="flashcard-question"
          >
            <div className="label-eyebrow text-[#9bc7ff] mb-2 flex items-center gap-1">
              <Sparkles size={11} /> Question {card.is_new ? "· New" : ""}
            </div>
            <div className="flex-1 grid place-items-center">
              <div className="text-white text-[17px] font-display leading-snug text-center px-2">
                {card.q}
              </div>
            </div>
            {card.hint && (
              <div className="text-[11px] text-white/45 italic line-clamp-1 mt-2">
                Hint: {card.hint}
              </div>
            )}
            <div className="text-[10px] text-white/30 font-mono-oak text-center mt-3 flex items-center justify-center gap-1.5">
              <RotateCw size={10} /> Tap to flip
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="a"
            initial={{ opacity: 0, rotateY: 12 }}
            animate={{ opacity: 1, rotateY: 0 }}
            exit={{ opacity: 0, rotateY: -12 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 p-6 flex flex-col"
            data-testid="flashcard-answer"
          >
            <div className="label-eyebrow text-[#00FF9D] mb-2 flex items-center gap-1">
              <Check size={11} /> Answer
            </div>
            <div className="flex-1 overflow-auto">
              <div className="text-white text-[15px] leading-relaxed">
                {card.a}
              </div>
            </div>
            <div className="text-[10px] text-white/30 font-mono-oak text-center mt-2">
              Rate how well you knew it →
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

const TONE_CLASSES = {
  rose:   "border-rose-400/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-100",
  amber:  "border-amber-400/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-100",
  cobalt: "border-[#0044FF]/45 bg-[#0044FF]/12 hover:bg-[#0044FF]/22 text-[#bcd1ff]",
  mint:   "border-[#00FF9D]/45 bg-[#00FF9D]/10 hover:bg-[#00FF9D]/22 text-[#9bffcc]",
};

function RateButton({ id, label, hint, tone, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`py-2.5 rounded-xl border font-display text-sm transition disabled:opacity-50 disabled:cursor-wait flex flex-col items-center justify-center gap-0.5 ${TONE_CLASSES[tone]}`}
      data-testid={`flashcard-rate-${id}`}
    >
      <span>{label}</span>
      <span className="text-[9.5px] opacity-65 font-mono-oak">{hint}</span>
    </button>
  );
}

function EmptyDeckState({ mode, onClose }) {
  return (
    <div className="text-center max-w-sm" data-testid="flashcard-empty">
      <div className="h-14 w-14 mx-auto rounded-2xl bg-white/[0.04] border border-white/10 grid place-items-center mb-3">
        <Brain size={20} className="text-white/45" />
      </div>
      <div className="font-display text-lg text-white mb-1.5">
        {mode === "step" ? "No flashcards yet" : "Nothing due today"}
      </div>
      <p className="text-[12.5px] text-white/55 leading-relaxed mb-4">
        {mode === "step"
          ? "Generate the lesson first — flashcards pull from your whitepaper content."
          : "Read a lesson and tap \"Make flashcards\" to build your first deck. New cards appear here for daily review."}
      </p>
      <button
        onClick={onClose}
        className="text-[11px] btn-mint-outline px-4 py-2 rounded-full"
        data-testid="flashcard-empty-close"
      >
        Got it
      </button>
    </div>
  );
}

function DoneState({ reviewedCount, creditGranted, onClose }) {
  const xpEarned = reviewedCount * 5;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="text-center max-w-sm"
      data-testid="flashcard-done"
    >
      <motion.div
        className="h-16 w-16 mx-auto rounded-2xl bg-gradient-to-br from-[#00FF9D]/25 to-[#0044FF]/25 border border-[#00FF9D]/40 grid place-items-center mb-3"
        animate={{ rotate: [0, -8, 8, -4, 0] }}
        transition={{ duration: 0.8 }}
      >
        <Sparkles size={22} className="text-[#9bffcc]" />
      </motion.div>
      <div className="font-display text-xl text-white mb-1">Deck complete</div>
      <p className="text-[12.5px] text-white/60 leading-relaxed mb-3">
        You reviewed <span className="text-white">{reviewedCount}</span> card{reviewedCount === 1 ? "" : "s"}{" "}
        and earned <span className="text-[#9bffcc]">+{xpEarned} XP</span>
        {creditGranted > 0 && (
          <> plus <span className="text-[#9bffcc]">+{creditGranted} credit</span> for completing today's deck.</>
        )}
      </p>
      <button
        onClick={onClose}
        className="text-[12px] btn-primary px-5 py-2 rounded-full font-display font-semibold"
        data-testid="flashcard-done-close"
      >
        Done
      </button>
    </motion.div>
  );
}
